import React, { useState, useMemo, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { format } from 'date-fns';
import { formatCurrency } from '../../../../utils/formatCurrency';

/**
 * @param {Object} props
 * @param {LancamentoAvulsoRow[]} [props.lista]
 * @param {(row: LancamentoAvulsoRow) => void} props.onSelecionar
 * @param {() => void} props.onNovo
 * @param {(boletoRecorrenciaId: string, targetWindow?: Window|null) => void} [props.onAbrirBoleto]
 * @param {string|null} [props.boletoLoadingId]
 */
export default function TabelaLancamentosAvulsos({ lista = [], infoUsuario, onSelecionar, onNovo, onAbrirBoleto, boletoLoadingId }) {

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(5);

  const situacaoLabelMap = useMemo(
    () => ({
      vencido: 'Vencido',
      cadastrado: 'Cadastrado',
      aguardando: 'Aguardando',
      pago: 'Pago',
      faturado: 'Faturado',
      cancelado: 'Cancelado',
      aberto: 'Aberto',
    }),
    []
  );

  const situacaoClassMap = useMemo(
    () => ({
      vencido:
        'bg-red-100 text-red-800 dark:bg-red-800/30 dark:text-red-500 ring-red-500 ring-1 ring-inset',
      cadastrado:
        'bg-blue-100 text-blue-800 dark:bg-blue-800/30 dark:text-blue-500 ring-blue-500 ring-1 ring-inset',
      aguardando:
        'bg-blue-100 text-blue-800 dark:bg-blue-800/30 dark:text-blue-500 ring-blue-500 ring-1 ring-inset',
      pago:
        'bg-teal-100 text-teal-800 dark:bg-teal-800/30 dark:text-teal-500 ring-teal-500 ring-1 ring-inset',
      faturado:
        'bg-yellow-100 text-yellow-800 dark:bg-yellow-800/30 dark:text-yellow-500 ring-yellow-500 ring-1 ring-inset',
      cancelado:
        'bg-gray-100 text-gray-800 dark:bg-white/10 dark:text-white ring-gray-400/40 ring-1 ring-inset',
      aberto:
        'bg-indigo-100 text-indigo-800 dark:bg-indigo-800/30 dark:text-indigo-300 ring-indigo-500 ring-1 ring-inset',
    }),
    []
  );

  /** @param {LancamentoAvulsoRow} rowData */
  const statusBoleto = useCallback(
    (rowData) => {
      const raw = (rowData?.situacao_exibicao ?? rowData?.lancamento_situacao ?? '')
        .toString()
        .toLowerCase()
        .trim();

      const label = situacaoLabelMap[raw] || (raw ? raw.replace(/_/g, ' ') : '—');
      const klass =
        situacaoClassMap[raw] ||
        'bg-gray-100 text-gray-800 dark:bg-white/10 dark:text-white ring-gray-400/40 ring-1 ring-inset';

      return (
        <span className={`inline-flex items-center justify-center rounded-sm ${klass} px-2 py-1 text-xs font-medium w-full`}>
          {label}
        </span>
      );
    },
    [situacaoClassMap, situacaoLabelMap]
  );

  /** @param {string} value */
  const formatCreatedAt = useCallback((value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return format(d, 'dd/MM/yyyy HH:mm');
  }, []);

  /** @param {LancamentoAvulsoRow} row */
  const boletoActions = useCallback(
    (row) => {
      const boletoId = row?.boleto_recorrencia_id;
      const isLoading = !!boletoId && boletoLoadingId === boletoId;

      const disabled = !boletoId || typeof onAbrirBoleto !== 'function' || isLoading;

      return (
        <Button
          type="button"
          label={isLoading ? 'Gerando...' : 'Abrir'}
          icon={isLoading ? 'pi pi-spin pi-spinner' : 'pi pi-file-pdf'}
          size="small"
          severity="secondary"
          disabled={disabled}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            // abre aba imediatamente (evita popup blocker)
            const w = window.open('about:blank', '_blank');
            onAbrirBoleto?.(boletoId, w);
          }}
        />
      );
    },
    [onAbrirBoleto, boletoLoadingId]
  );

  return (
    <DataTable
      value={Array.isArray(lista) ? lista : []}
      dataKey="lancamento_id"
      paginator
      rows={rows}
      first={first}
      onPage={(e) => {
        setFirst(e.first);
        setRows(e.rows);
      }}
      paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
      currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} lançamentos"
      rowsPerPageOptions={[5, 10, 20, 50]}
      emptyMessage="Nenhum lançamento cadastrado."
      selectionMode="single"
      onSelectionChange={(e) => onSelecionar?.(e.value)}
      scrollable
      scrollHeight="flex"
      tableStyle={{ minWidth: '100%' }}
      header={
        <div className="flex justify-between items-center px-2 py-2">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Lançamentos avulsos - {infoUsuario.usuario_nome}</h2>
          <Button icon="pi pi-plus" label="Novo" severity="success" size="small" onClick={onNovo} />
        </div>
      }
    >
      <Column
        header="Situação"
        body={statusBoleto}
        style={{ width: '10.5rem', textAlign: 'center' }}
        bodyStyle={{ textAlign: 'center', padding: '0.5rem' }}
      />

      <Column
        header="Descrição"
        body={(row) => (
          <span title={row?.lancamento_descricao ?? ''} className="line-clamp-1">
            <strong className="text-sm">{row?.lancamento_descricao ?? '—'}</strong>
          </span>
        )}
      />

      <Column
        header="Valor"
        body={(row) => (
          <span>
            <strong className="text-sm">{formatCurrency(row?.lancamento_valor ?? 0)}</strong>
          </span>
        )}
        style={{ width: '9rem' }}
      />

      <Column
        header="Referência"
        body={(row) => (
          <strong className="text-sm">
            {String(row?.mes_referencia ?? '').padStart(2, '0')}/{row?.ano_referencia ?? '—'}
          </strong>
        )}
        style={{ width: '7.5rem', textAlign: 'center' }}
        bodyStyle={{ textAlign: 'center' }}
      />

      <Column
        header="Vencimento"
        body={(row) => <strong className="text-sm">{row?.vencimento_exibicao ?? '-'}</strong>}
        style={{ width: '8.5rem' }}
      />

      <Column
        header="Cadastro"
        body={(row) => <strong className="text-sm">{formatCreatedAt(row?.created_at)}</strong>}
        style={{ width: '10.5rem' }}
      />

      <Column header="Boleto" body={boletoActions} style={{ width: '7.5rem', textAlign: 'center' }} bodyStyle={{ textAlign: 'center' }} />
    </DataTable>
  );
}
